/**
 * schema.js — JSON-LD schema.org blocks used by `<StructuredData />`.
 *
 * Each export is a plain JS object serializable straight to JSON.
 * Keep this file dependency-free so it's cheap to import from any page.
 *
 * Reference: https://schema.org/  ·  https://developers.google.com/search/docs/appearance/structured-data
 */

const SITE_URL = "https://www.meliandialogue.com";
const ORG_REF  = { "@id": `${SITE_URL}/#organization` };

/**
 * Landing page — SoftwareApplication (primary) plus a Product for each
 * subscription tier. Google prefers `Product` for `aggregateRating` and
 * `offers`, and `SoftwareApplication` for feature enumeration.
 */
export const SOFTWARE_APPLICATION_JSON_LD = {
    "@context":            "https://schema.org",
    "@type":               "SoftwareApplication",
    "@id":                 `${SITE_URL}/#app`,
    "name":                "Melian Dialogue",
    "alternateName":       ["Melian", "Melian Dialogue Platform"],
    "url":                 SITE_URL,
    "applicationCategory": "FinanceApplication",
    "applicationSubCategory": "TradingSimulator",
    "operatingSystem":     "Web · Progressive Web App (Chrome, Safari, Firefox, Edge)",
    "browserRequirements": "Requires modern browser with WebSocket + IndexedDB support.",
    "inLanguage":          ["en", "sw", "fr", "ar", "pt"],
    "publisher":           ORG_REF,
    "description":
        "Simulated prop-trading platform for Pan-African + global markets with live-priced NSE / DSE / USE / RSE / ESX / NGX / JSE / EGX / BRVM / Morocco / Tunis universes, the Behavioral Coefficient Index (BeCo Index) analytics engine, SHA-256 cryptographic audit ledger, and 6-language multilingual UX including Arabic RTL.",
    "featureList": [
        "Live-priced Pan-African stock universe (Kenya NSE, Tanzania DSE, Uganda USE, Rwanda RSE, Ethiopia ESX, Nigeria NGX, South Africa JSE, Egypt EGX, BRVM, Morocco, Tunis) via Mansa Markets",
        "US equity + crypto + FX price feed via Financial Modeling Prep (FMP)",
        "BeCo Index™ · 0-100 Behavioral Coefficient composite of 10 sub-metrics (α_B,1..5 discipline + σ_B,1..5 risk-inflation)",
        "SHA-256 cryptographically chained tamper-proof audit ledger with atomic append",
        "Gamified 3-scope leaderboards (Master / Alpha / Sigma) with deterministic tie-break",
        "128-dimensional behavioral embeddings for pre-tilt clustering",
        "6-language UI localization with full Arabic RTL layout mirroring",
        "Obondo AI trading copilot with voice + graph modes (subscription tier)",
        "Kimani rule-based FAQ chatbot (all tiers)",
        "11-layer cognitive guardrail stack including Rule-11 hard 2% risk throttle",
        "20-trait psychometric baseline diagnostic",
        "Institutional multi-seat admin dashboard with bulk CSV seat import",
        "Super-admin CSV + PDF audit-chain export (Playwright)",
        "Real-time WebSocket market ticks + PnL + BeCo updates",
        "Dark / light mode + Cmd+K palette + PWA offline shell",
    ],
    "offers": [
        {
            "@type": "Offer",
            "name":  "Kimani (Free forever)",
            "description":
                "Free forever plan. Full trading terminal, 130+ live-priced instruments, community feed, public leaderboard.",
            "price": "0",
            "priceCurrency": "KES",
            "availability": "https://schema.org/InStock",
            "eligibleRegion": ["KE", "TZ", "UG", "RW", "ET", "NG", "ZA", "EG", "MA", "TN"],
        },
        {
            "@type": "Offer",
            "name":  "Obondo Advanced",
            "description":
                "7-day free trial, then $19/month (or €17/month). Adds BeCo Index™, α_B / σ_B sub-metrics, global BeCo leaderboards, 128-D behavioral embeddings, 6-language + Arabic RTL, Obondo AI coach.",
            "price": "19.00",
            "priceCurrency": "USD",
            "eligibleDuration": {
                "@type": "QuantitativeValue",
                "value": 7,
                "unitCode": "DAY",
                "name":    "Free trial"
            },
            "availability": "https://schema.org/InStock",
        },
        {
            "@type": "Offer",
            "name":  "CMA Institutional (annual)",
            "description":
                "Annual bank-transfer subscription for CMA-licensed brokers, dealers, and fund managers. Up to 100 seats included. Multi-seat admin dashboard, bulk CSV import, private agency leaderboards, super-admin audit-chain export.",
            "price": "500000",
            "priceCurrency": "KES",
            "eligibleDuration": {
                "@type": "QuantitativeValue",
                "value": 12,
                "unitCode": "MON",
                "name":    "Annual"
            },
            "availability": "https://schema.org/InStock",
            "eligibleRegion": { "@type": "Country", "name": "Kenya" }
        },
        {
            "@type": "Offer",
            "name":  "MoE Institution (annual)",
            "description":
                "Annual bank-transfer subscription for schools, colleges, and universities registered with Kenya's Ministry of Education. Up to 100 seats included.",
            "price": "350000",
            "priceCurrency": "KES",
            "eligibleDuration": {
                "@type": "QuantitativeValue",
                "value": 12,
                "unitCode": "MON",
                "name":    "Annual"
            },
            "availability": "https://schema.org/InStock",
            "eligibleRegion": { "@type": "Country", "name": "Kenya" }
        }
    ],
};

/**
 * About page — Organization info + AboutPage wrapper.
 */
export const ABOUT_PAGE_JSON_LD = {
    "@context": "https://schema.org",
    "@type":    "AboutPage",
    "@id":      `${SITE_URL}/about#page`,
    "url":      `${SITE_URL}/about`,
    "name":     "About Melian Dialogue",
    "description":
        "Melian Dialogue Limited is a BRS-registered Kenyan software company operating a simulated prop-trading platform with proprietary behavioral analytics.",
    "isPartOf": { "@id": `${SITE_URL}/#website` },
    "about":    ORG_REF,
    "primaryImageOfPage": {
        "@type": "ImageObject",
        "url":   `${SITE_URL}/logo512.svg`,
    },
};

/**
 * Contact page — ContactPage.
 */
export const CONTACT_PAGE_JSON_LD = {
    "@context": "https://schema.org",
    "@type":    "ContactPage",
    "@id":      `${SITE_URL}/contact#page`,
    "url":      `${SITE_URL}/contact`,
    "name":     "Contact Melian Dialogue",
    "isPartOf": { "@id": `${SITE_URL}/#website` },
    "about":    ORG_REF,
};

/**
 * Build a FAQPage schema block from the same `FAQ_CATEGORIES` array
 * that renders the `/faq` UI. Guarantees the visible content matches
 * what crawlers index (Google fails the rich result otherwise).
 *
 * NOTE: Markdown syntax (**bold**, links) is stripped before serialization
 * so plain-text answers survive Google's Rich Results validator.
 */
function stripMarkdown(md) {
    if (!md) return "";
    return String(md)
        // Bold / italic markers
        .replace(/\*\*(.+?)\*\*/g, "$1")
        .replace(/\*(.+?)\*/g, "$1")
        .replace(/`([^`]+)`/g, "$1")
        // Markdown links → link text only
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        // Bullet dashes at line-start
        .replace(/^[-•]\s+/gm, "")
        // Collapse consecutive whitespace
        .replace(/\s+/g, " ")
        .trim();
}

export function buildFaqJsonLd(faqCategories) {
    const mainEntity = [];
    for (const cat of faqCategories || []) {
        for (const q of cat.entries || []) {
            mainEntity.push({
                "@type": "Question",
                "name":  stripMarkdown(q.q),
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text":  stripMarkdown(q.a),
                },
            });
        }
    }
    return {
        "@context":   "https://schema.org",
        "@type":      "FAQPage",
        "@id":        `${SITE_URL}/faq#page`,
        "url":        `${SITE_URL}/faq`,
        "name":       "Melian Dialogue — Frequently Asked Questions",
        "isPartOf":   { "@id": `${SITE_URL}/#website` },
        "about":      ORG_REF,
        "inLanguage": "en",
        "mainEntity": mainEntity,
    };
}

/**
 * Common breadcrumb builder — array of `{name, url}` in root→leaf order.
 */
export function buildBreadcrumbsJsonLd(crumbs) {
    return {
        "@context":        "https://schema.org",
        "@type":           "BreadcrumbList",
        "itemListElement": crumbs.map((c, i) => ({
            "@type":    "ListItem",
            "position": i + 1,
            "name":     c.name,
            "item":     c.url.startsWith("http") ? c.url : `${SITE_URL}${c.url}`,
        })),
    };
}
